import { useCallback, useEffect, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";

export type InviteStatus = "PENDING" | "ACCEPTED" | "REVOKED";

export type Invite = {
  email: string;
  status: InviteStatus;
  invitedAt: string;
  invitedBy: string;
  acceptedAt: string | null;
};

export type InvitesStatus = "loading" | "ready" | "error";

export type InviteErrorCode = "DUPLICATE" | "INVALID" | "NOT_FOUND" | "INVALID_STATE" | "UNAUTHORIZED" | "UNKNOWN";

export type InviteResult<T> =
  | { ok: true; value: T }
  | { ok: false; code: InviteErrorCode; message: string };

export type UseInvitesResult = {
  status: InvitesStatus;
  invites: ReadonlyArray<Invite>;
  error: string | null;
  createInvite: (email: string) => Promise<InviteResult<Invite>>;
  revokeInvite: (email: string) => Promise<InviteResult<void>>;
  refresh: () => Promise<void>;
};

const LIST_URL = "/api/admin/invites";

type ServerError = { code: string; message: string };

const INVITE_ERROR_CODES: ReadonlyArray<InviteErrorCode> = [
  "DUPLICATE", "INVALID", "NOT_FOUND", "INVALID_STATE", "UNAUTHORIZED", "UNKNOWN",
];

const narrowCode = (code: string): InviteErrorCode =>
  INVITE_ERROR_CODES.includes(code as InviteErrorCode) ? (code as InviteErrorCode) : "UNKNOWN";

export const useInvites = (): UseInvitesResult => {
  const [status, setStatus] = useState<InvitesStatus>("loading");
  const [invites, setInvites] = useState<ReadonlyArray<Invite>>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch(LIST_URL, { credentials: "include" });
      if (res.status === 200) {
        const body = (await res.json()) as Invite[];
        setInvites(body);
        setStatus("ready");
        setError(null);
      } else {
        setStatus("error");
        setError(`List failed (${res.status})`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setStatus("error");
      setError(message);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const createInvite = useCallback(async (email: string): Promise<InviteResult<Invite>> => {
    try {
      const res = await fetch(LIST_URL, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...getCsrfHeader(),
        },
        body: JSON.stringify({ email }),
      });

      if (res.status === 201) {
        const invite = (await res.json()) as Invite;
        setInvites((prev) => {
          const without = prev.filter((i) => i.email !== invite.email);
          return [invite, ...without];
        });
        return { ok: true, value: invite };
      }

      if (res.status === 401) {
        return { ok: false, code: "UNAUTHORIZED", message: "Session expired. Refresh to sign in again." };
      }

      if (res.status === 400 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        return {
          ok: false,
          code: narrowCode(body?.code ?? "UNKNOWN"),
          message: body?.message ?? `Invite failed (${res.status})`,
        };
      }

      return { ok: false, code: "UNKNOWN", message: `Invite failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, []);

  const revokeInvite = useCallback(async (email: string): Promise<InviteResult<void>> => {
    try {
      const res = await fetch(`${LIST_URL}/${encodeURIComponent(email)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });

      if (res.status === 204) {
        setInvites((prev) => prev.map((i) => (i.email === email ? { ...i, status: "REVOKED" } : i)));
        return { ok: true, value: undefined };
      }

      if (res.status === 401) {
        return { ok: false, code: "UNAUTHORIZED", message: "Session expired. Refresh to sign in again." };
      }

      if (res.status === 404 || res.status === 409) {
        const body = (await res.json().catch(() => null)) as ServerError | null;
        return {
          ok: false,
          code: narrowCode(body?.code ?? "UNKNOWN"),
          message: body?.message ?? `Revoke failed (${res.status})`,
        };
      }

      return { ok: false, code: "UNKNOWN", message: `Revoke failed (${res.status})` };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      return { ok: false, code: "UNKNOWN", message };
    }
  }, []);

  return { status, invites, error, createInvite, revokeInvite, refresh };
};
